import { BefehlAuftragDto, BefehlFeldDto } from '../../../dtos'

export function createField(
  kennung: string,
  werte: Record<string, unknown> | string | null
): BefehlFeldDto {
  return { kennung, werte }
}

export function createZugmeldestelleField(
  kennung: string,
  art: string | undefined | null,
  name: string | undefined | null
): BefehlFeldDto {
  return {
    kennung,
    werte: {
      zugmeldestelle: {
        art: art ?? '',
        name: name ?? ''
      }
    }
  }
}

export function createKmField(kennung: string, km: string): BefehlFeldDto {
  return {
    kennung,
    werte: { km: km ?? '' }
  }
}

export function createSignalField(
  kennung: string,
  signal: string
): BefehlFeldDto {
  return {
    kennung,
    werte: { signal: signal ?? '' }
  }
}

export function createFreitextField(
  kennung: string,
  text: string
): BefehlFeldDto {
  return {
    kennung,
    werte: { freitext: text ?? '' }
  }
}

/**
 * Always returns a field, even if kmh is empty - kmh fields are required by backend
 */
export function createKmhField(
  kennung: string,
  kmh: string | undefined | null
): BefehlFeldDto {
  return {
    kennung,
    werte: { kmh: kmh ?? '' }
  }
}

export function filterNullFields(
  fields: Array<BefehlFeldDto | null>
): BefehlFeldDto[] {
  return fields.filter((field): field is BefehlFeldDto => field !== null)
}

export function createSimpleAuftrag(
  kennung: string | undefined | null,
  enabled: boolean
): BefehlAuftragDto | null {
  if (!enabled) return null

  return {
    kennung: kennung!,
    felder: []
  }
}

export function isSelectorInOrZwischen(
  selector: string | undefined | null
): boolean {
  return selector === 'in' || selector === 'zwischen'
}

export function createSignalWithZugmeldestelleField(
  kennung: string,
  signal: string,
  art: string,
  name: string
): BefehlFeldDto {
  return {
    kennung,
    werte: {
      signal: signal ?? '',
      zugmeldestelle: {
        art: art ?? '',
        name: name ?? ''
      }
    }
  }
}

export function findAuftragByKennung<T extends BefehlAuftragDto>(
  auftraege: T[] | undefined | null,
  kennung: string | undefined | null
): T | undefined {
  if (!kennung) return undefined
  return auftraege?.find((a) => a.kennung === kennung)
}

export function hasAuftrag(
  auftraege: BefehlAuftragDto[] | undefined | null,
  kennung: string | undefined | null
): boolean {
  return !!findAuftragByKennung(auftraege, kennung)
}

type KmSignalEmpty = 'km' | 'signal' | ''
/**
 * For km type: appends '.1' to kennung
 * For signal type: appends '.2' to kennung
 */
export function createLocationField(
  baseKennung: string,
  type: KmSignalEmpty,
  kmValue: string,
  signalValue: string
): BefehlFeldDto | null {
  if (type === 'km') {
    return createKmField(`${baseKennung}.1`, kmValue)
  }
  if (type === 'signal') {
    return createSignalField(`${baseKennung}.2`, signalValue)
  }
  return null
}

export function createAuftrag(kennung: string, fields: BefehlFeldDto[]) {
  return {
    kennung,
    felder: fields
  }
}

export const buildField = (config: {
  kennung: string
  value: Record<string, unknown> | string | null
  isEmpty?: (value: unknown) => boolean
}) => {
  return createField(config.kennung, config.value)
}
export const buildZugmeldestelleField = (
  kennung: string,
  data?: { art: string; name: string }
) => createZugmeldestelleField(kennung, data?.art ?? '', data?.name ?? '')
export const buildFreitextField = (kennung: string, freitext?: string) =>
  createFreitextField(kennung, freitext ?? '')
export const buildLocationField = (config: {
  baseKennung: string
  type: KmSignalEmpty
  kmValue?: string
  signalValue?: string
}) => {
  if (config.type === 'km') {
    return createKmField(`${config.baseKennung}.1`, config.kmValue || '')
  }
  if (config.type === 'signal') {
    return createSignalField(
      `${config.baseKennung}.2`,
      config.signalValue || ''
    )
  }
  return null
}

export function createFreitextAuftrag(
  befehlNummer: string,
  enabled: boolean,
  freitext: string | undefined | null
): BefehlAuftragDto | null {
  if (!enabled) return null

  return {
    kennung: `${befehlNummer}.95`,
    felder: [
      {
        kennung: `${befehlNummer}.96`,
        werte: { freitext: freitext ?? '' }
      }
    ]
  }
}

// Simplified versions of complex functions
export function buildAuftrag(config: {
  kennung: string
  enabled: boolean
  fieldBuilders: Array<() => BefehlFeldDto | null>
}) {
  if (!config.enabled) return null
  const fields = config.fieldBuilders
    .map((builder) => builder())
    .filter((f) => f !== null)
  return createAuftrag(config.kennung, fields)
}

export function buildConditionalFields(
  configs: Array<{
    condition: boolean
    fieldBuilder: () => BefehlFeldDto | null
  }>
) {
  return configs
    .filter((config) => config.condition)
    .map((config) => config.fieldBuilder)
}

export function buildBidirectionalLocationFields(
  selector: string,
  field47Config: {
    baseKennung: string
    type: KmSignalEmpty
    kmValue?: string
    signalValue?: string
  },
  field48Config: {
    baseKennung: string
    type: KmSignalEmpty
    kmValue?: string
    signalValue?: string
  }
) {
  if (!isSelectorInOrZwischen(selector)) return []

  return [
    () =>
      createLocationField(
        field47Config.baseKennung,
        field47Config.type,
        field47Config.kmValue || '',
        field47Config.signalValue || ''
      ),
    () =>
      createLocationField(
        field48Config.baseKennung,
        field48Config.type,
        field48Config.kmValue || '',
        field48Config.signalValue || ''
      )
  ]
}
